import type { ChatImage } from './types';
import type { Content, KeyTextField, RichTextField, RTNode } from '@prismicio/client';
import { formatString } from '@/i18n/catalogue';

/**
 * The `discovery` slice, as the chat engine reads it.
 *
 * A discovery is one step the learner works through at their own pace: a list
 * of named entries, each of which shows a picture and some facts and then
 * takes itself off the list. It is the only prompt that re-arms itself — see
 * `advanceDiscovery` — which is why the rules for what one pick lands, and for
 * what is left afterwards, live here as pure functions rather than in the
 * reducer.
 */

export type DiscoverySlice = Content.DiscoverySlice;

/**
 * One thing the learner can discover.
 *
 * `id` is positional (`<slice id>-e0`), like every other option id in the
 * engine: a saved run stores the *position* it picked, so the ids never have to
 * survive a language switch.
 */
export type DiscoveryEntry = {
  id: string;
  name: KeyTextField;
  /** The photo, ready for the thread's image message and its sheet. */
  image: ChatImage;
  /** The facts, one field per bubble — see `detailBubbles`. */
  details: Array<RichTextField>;
};

/**
 * Splits authored detail text into one field per **bubble**.
 *
 * Editors write the facts about an entry as ordinary paragraphs in a single
 * rich text field, and the coach says them one after another — which is what
 * makes a discovery read like a conversation instead of a wall of text. A list
 * belongs to the paragraph above it, so a bulleted fact stays whole.
 *
 * Blank paragraphs are dropped rather than landing an empty bubble.
 */
export function detailBubbles(field: RichTextField): Array<RichTextField> {
  const nodes: ReadonlyArray<RTNode> = field;
  const bubbles: Array<[RTNode, ...Array<RTNode>]> = [];

  for (const node of nodes) {
    const continues = node.type === 'list-item' || node.type === 'o-list-item';
    const current = bubbles.at(-1);

    if (continues && current) {
      current.push(node);
      continue;
    }

    // A paragraph with no words and no picture is authoring whitespace.
    if (node.type === 'paragraph' && node.text.trim() === '') {
      continue;
    }

    bubbles.push([node]);
  }

  return bubbles;
}

/** The entries of a discovery slice, in the order the editor listed them. */
export function discoveryEntries(slice: DiscoverySlice): Array<DiscoveryEntry> {
  return slice.primary.items.map((item, index) => ({
    id: `${slice.id}-e${index}`,
    name: item.name,
    image: {
      image: item.image,
      // The caption is optional: an entry is already named by the button the
      // learner just pressed, so repeating the name is the sensible default.
      caption: item.caption?.trim() ? item.caption : item.name,
      description: item.details,
    },
    details: detailBubbles(item.details),
  }));
}

/**
 * The counter line under an entry, or `null` when the editor left the template
 * blank — which is how a short set (three entries, say) opts out of a count it
 * does not need.
 */
export function discoveryProgress(template: KeyTextField, done: number, total: number): string | null {
  const text = template?.trim();

  if (text === undefined || text === '') {
    return null;
  }

  return formatString(text, { done, total, left: total - done });
}
